import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCreateMessageMutation } from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import type { MessageInteractionPhase, Message, ModelOption } from "./types";
import type { Option } from "../../../components/tailwind/dropdown/Dropdown";
import { preventOverflowOnTextarea } from "../../../products/copy-assistant/utils";
import { Button, Dropdown } from "../../../components/tailwind";

type ChatMessageFormProps = {
  chatId: number;
  chatRequestTaskQue: ChatRequestTaskQue;
  modelOptions: ModelOption[];
  initialModel: ModelOption;
  createMessage: ReturnType<typeof useCreateMessageMutation>[0];
  interactionPhase: MessageInteractionPhase;
  setResponsePhase: React.Dispatch<
    React.SetStateAction<MessageInteractionPhase>
  >;
  onMessageAdded: (newMessage: Message) => void;
  onResponseReceived: () => void;
};

export const ChatMessageForm: React.FC<ChatMessageFormProps> = ({
  chatId,
  chatRequestTaskQue,
  modelOptions: modelOptionItems,
  initialModel,
  createMessage,
  interactionPhase,
  setResponsePhase,
  onMessageAdded,
  onResponseReceived,
}) => {
  const [selectedModel, setSelectedModel] = useState<ModelOption>(initialModel);
  const [inputText, setInputText] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>();

  const modelOptions: Option<string>[] = useMemo(
    () =>
      modelOptionItems.map((item) => ({
        key: item.model_name,
        value: item.model_name,
        text: item.display_name,
      })),
    []
  );

  useEffect(() => {
    handleModelSelect(initialModel.model_name);
  }, [chatId]);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      preventOverflowOnTextarea(textareaRef.current);
    }
  }, []);

  const handleModelSelect = (modelName: string): void => {
    setSelectedModel(
      modelOptionItems.find((item) => item.model_name === modelName)
    );
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!inputText.trim() || interactionPhase === "awaiting reply") {
      return;
    }

    setInputText("");
    setResponsePhase("creating message");

    try {
      const response = await createMessage({
        chat_id: chatId,
        text: inputText,
        model_name: selectedModel.model_name,
      }).unwrap();
      setResponsePhase("awaiting reply");

      const createdMessage = response.created_message;
      const valueError = response.value_error;
      const error = response.error;

      if (error) {
        throw new Error(error);
      } else if (valueError) {
        throw new Error(valueError);
      }

      const userMessage: Message = {
        role: "user",
        text: createdMessage.text,
        timeStamp: createdMessage.timestamp,
      };
      onMessageAdded(userMessage);

      const { response_message } = await chatRequestTaskQue.requestResponse(
        chatId
      );

      if (!response_message) {
        throw new Error("No response message received from chat");
      }

      const assistantMessage: Message = {
        role: "assistant",
        text: response_message.text,
        timeStamp: response_message.timestamp,
      };

      onMessageAdded(assistantMessage);
      onResponseReceived();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setResponsePhase("idle");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="tw-grow-0 tw-p-4 tw-rounded-md tw-border tw-shadow-lg"
      id="chat-message-form"
    >
      <textarea
        value={inputText}
        ref={textareaRef}
        onChange={(e) => setInputText(e.target.value)}
        className="tw-m-1 !tw-p-0 tw-resize-none tw-border !tw-ring-0"
        disabled={interactionPhase !== "idle"}
      />
      <div className="tw-flex tw-justify-between">
        <div className="tw-flex tw-gap-4">
          <Dropdown
            options={modelOptions}
            value={selectedModel?.model_name || null}
            onChange={handleModelSelect}
          />
        </div>
        <Button
          type="submit"
          disabled={!inputText.trim() || interactionPhase !== "idle"}
          variant="textual-blue"
        >
          Send
        </Button>
      </div>
    </form>
  );
};
